import { v4 as uuid } from "uuid";

import { useEffect, useState } from "react";
import { DataSheetGrid } from "react-datasheet-grid";

import "./styles.css";
import { Operation } from "react-datasheet-grid/dist/types";

import {
  SocialProcurementEntityType,
  SocialProcurementLineItem,
  createDefaultSocialProcurement,
} from "@interfaces/socialProcurement";
import { PlaceLatLng } from "@interfaces/location";
import { SocialCommitmentTypeSpend, TargetUnit } from "@interfaces/commitment";
import { validatePostCode } from "@utils/location";
import { Button, Grid, Typography } from "@mui/material";
import { StyledLoadingButton } from "@stories/atoms/StyledButton/StyledButton";
import { getSocialProcurementColumns } from "./SocialProcurementColumns";
import { ContractorPackageLineItem } from "@interfaces/contractorPackage";
import { validateABN } from "@utils/abn";

export interface SocialProcurementTableRow {
  id: string;
  reportId: string;
  transactionDate: Date;
  entityType: SocialProcurementEntityType;
  supplier: string;
  abn: string;
  supplierCountry: string;
  supplyPostCode: string;
  geoLocation: PlaceLatLng;
  invoiceReference: string;
  natureOfExpenditure: string;
  invoiceValue: number;
  copyOfInvoice: string;
  comments: string;
  socialFactors: SocialCommitmentTypeSpend[];
  empty?: boolean;
  dirty?: boolean;
  invalid?: boolean;
}

interface SocialProcurementSheetProps {
  loading: boolean;
  projectId: string;
  reportId: string;
  contractorPackage: ContractorPackageLineItem;
  socialProcurements: SocialProcurementLineItem[];
  handleUpsertSocialProcurements: (
    labourHours: SocialProcurementLineItem[]
  ) => void;
  handleDeleteSocialProcurements: (
    labourHours: SocialProcurementLineItem[]
  ) => void;
}

export const SocialProcurementSheet = ({
  loading,
  projectId,
  reportId,
  contractorPackage,
  socialProcurements,
  handleUpsertSocialProcurements,
  handleDeleteSocialProcurements,
}: SocialProcurementSheetProps) => {
  const [data, setData] = useState<SocialProcurementTableRow[]>([]);

  if (data.length < 10) {
    for (let i = 0; i < 10 - data.length; i++) {
      data.push({ id: uuid(), empty: true } as SocialProcurementTableRow);
    }
    setData(data);
  } else {
    if (data.length > 0) {
      if (!data[data.length - 1].empty) {
        data.push({ id: uuid(), empty: true } as SocialProcurementTableRow);
      }
    }
  }

  const regenData = () => {
    const socialProcurementRowItems = socialProcurements.map((s) => {
      const newTableRow = {
        id: s.id,
        reportId: s.reportId,
        transactionDate: s.transactionDate,
        entityType: s.entityType,
        supplier: s.supplier,
        abn: s.abn,
        supplierCountry: s.supplierCountry,
        supplyPostCode: s.supplyPostCode,
        geoLocation: s.geoLocation,
        invoiceReference: s.invoiceReference,
        natureOfExpenditure: s.natureOfExpenditure,
        invoiceValue: s.invoiceValue,
        copyOfInvoice: s.copyOfInvoice,
        comments: s.comments,
        socialFactors: s.socialFactors,
        dirty: false,
        invalid: false,
        empty: false,
      } as SocialProcurementTableRow;
      for (const sf of commitments) {
        (newTableRow as unknown as any)[sf.id] = s.socialFactors.find(
          (s) => sf.id === s
        );
      }
      return newTableRow;
    }) as SocialProcurementTableRow[];
    setData(socialProcurementRowItems);
  };
  useEffect(() => {
    regenData();
  }, [socialProcurements]);

  const [isUploading, setIsUploading] = useState(false);

  const commitments = contractorPackage.commitments.filter(
    (c) => c.targetUnit == TargetUnit.Spend
  );
  const columns = getSocialProcurementColumns(
    projectId,
    commitments,
    isUploading,
    setIsUploading,
    false
  );

  const validateSocialProcurementTableRow = (
    socialProcurement: SocialProcurementTableRow
  ) => {
    const validPostCode = socialProcurement.supplyPostCode
      ? validatePostCode(
          socialProcurement.supplierCountry,
          socialProcurement.supplyPostCode
        )
      : false;

    // const lngLat = await getLongitudeAndLatitude(
    //   socialProcurement.supplierCountry,
    //   socialProcurement.supplyPostCode,
    //   "",
    //   ""
    // );
    socialProcurement.geoLocation = { lng: 0, lat: 0 };
    return (
      !socialProcurement.transactionDate ||
      !socialProcurement.entityType ||
      !socialProcurement.supplier ||
      !socialProcurement.abn ||
      !validateABN(socialProcurement.abn) ||
      !socialProcurement.supplierCountry ||
      !validPostCode ||
      !socialProcurement.geoLocation ||
      !socialProcurement.invoiceReference ||
      !socialProcurement.natureOfExpenditure ||
      !socialProcurement.invoiceValue
    );
  };

  const isEmptySocialProcurementTableRow = (
    socialProcurement: SocialProcurementTableRow
  ) => {
    return (
      !socialProcurement.transactionDate ||
      !socialProcurement.entityType ||
      !socialProcurement.supplier ||
      !socialProcurement.abn ||
      !socialProcurement.supplierCountry ||
      !socialProcurement.supplyPostCode ||
      !socialProcurement.geoLocation ||
      !socialProcurement.invoiceReference ||
      !socialProcurement.natureOfExpenditure ||
      !socialProcurement.invoiceValue
    );
  };

  const [deletedRowIds, setDeletedRowIds] = useState(new Set<string>());
  const [updatedRowIds, setUpdatedRowIds] = useState(new Set<string>());

  const onDataChange = async (
    data: SocialProcurementTableRow[],
    operations: Operation[]
  ) => {
    data.forEach(async (d) => {
      d.empty = isEmptySocialProcurementTableRow(d);
      d.invalid = validateSocialProcurementTableRow(d);
    });

    for (const operation of operations) {
      if (operation.type === "CREATE" || operation.type === "UPDATE") {
        data
          .slice(operation.fromRowIndex, operation.toRowIndex)
          .forEach((d) => {
            d.dirty = true;
            setUpdatedRowIds((prev) => {
              if (d.invalid) {
                return prev;
              }
              return new Set(prev.add(d.id));
            });
          });
      }
      if (operation.type === "DELETE") {
        const tableIds = data.map((d) => d.id);
        data
          .slice(operation.fromRowIndex, operation.toRowIndex)
          .forEach((d) => {
            setDeletedRowIds((prev) => {
              const idsToDelete = socialProcurements
                .filter((sp) => !tableIds.includes(sp.id))
                .map((sp) => sp.id);
              for (const id of idsToDelete) {
                prev = prev.add(id);
              }
              return new Set(prev);
            });
          });
      }
    }
    setData(data);
  };

  const handleSave = async () => {
    const socialProcurementToUpsert = data.filter(
      (r) => r.id && updatedRowIds.has(r.id)
    );
    const validRows = socialProcurementToUpsert.filter(
      (r) => !r.invalid && !r.empty
    );

    const socialProcurementLineItemsToUpsert: SocialProcurementLineItem[] = [];
    validRows.forEach((e) => {
      const newSocialFactors = [];
      for (const sc of commitments) {
        if ((e as unknown as any)[sc.id]) {
          newSocialFactors.push(sc.id);
        }
      }

      const originalSocialProcurement = socialProcurements.find(
        (o) => o.id === e.id
      );
      if (originalSocialProcurement) {
        originalSocialProcurement.transactionDate = e.transactionDate;
        originalSocialProcurement.entityType = e.entityType;
        originalSocialProcurement.supplier = e.supplier;
        originalSocialProcurement.abn = e.abn;
        originalSocialProcurement.supplierCountry = e.supplierCountry;
        originalSocialProcurement.supplyPostCode = e.supplyPostCode;
        originalSocialProcurement.invoiceReference = e.invoiceReference;
        originalSocialProcurement.natureOfExpenditure = e.natureOfExpenditure;
        originalSocialProcurement.invoiceValue = e.invoiceValue;
        originalSocialProcurement.copyOfInvoice = e.copyOfInvoice;
        originalSocialProcurement.comments = e.comments;
        originalSocialProcurement.socialFactors =
          newSocialFactors as SocialCommitmentTypeSpend[];

        socialProcurementLineItemsToUpsert.push(originalSocialProcurement);
      } else {
        const defaultSocialProcurement =
          createDefaultSocialProcurement(reportId);
        socialProcurementLineItemsToUpsert.push({
          ...defaultSocialProcurement,
          id: e.id,
          transactionDate:
            e.transactionDate || defaultSocialProcurement.transactionDate,
          entityType: e.entityType || defaultSocialProcurement.entityType,
          supplier: e.supplier || defaultSocialProcurement.supplier,
          abn: e.abn || defaultSocialProcurement.abn,
          supplierCountry:
            e.supplierCountry || defaultSocialProcurement.supplierCountry,
          supplyPostCode:
            e.supplyPostCode || defaultSocialProcurement.supplyPostCode,
          geoLocation: { lat: 0, lng: 0 },
          invoiceReference:
            e.invoiceReference || defaultSocialProcurement.invoiceReference,
          natureOfExpenditure:
            e.natureOfExpenditure ||
            defaultSocialProcurement.natureOfExpenditure,
          invoiceValue: e.invoiceValue || defaultSocialProcurement.invoiceValue,
          copyOfInvoice:
            e.copyOfInvoice || defaultSocialProcurement.copyOfInvoice,
          comments: e.comments || defaultSocialProcurement.comments,
          socialFactors: newSocialFactors as SocialCommitmentTypeSpend[],
        });
      }
    });
    if (socialProcurementLineItemsToUpsert.length > 0) {
      await handleUpsertSocialProcurements(socialProcurementLineItemsToUpsert);
    }

    const socialProcurementToDelete = socialProcurements.filter((r) =>
      deletedRowIds.has(r.id)
    );
    if (socialProcurementToDelete.length > 0) {
      await handleDeleteSocialProcurements(socialProcurementToDelete);
    }
    setDeletedRowIds(new Set());
    setUpdatedRowIds(new Set());
  };
  return (
    <>
      <Grid container spacing={2} sx={{ padding: "10px" }}>
        <Grid item xs={12} md={12}>
          <DataSheetGrid<SocialProcurementTableRow>
            autoAddRow
            value={data}
            onChange={onDataChange}
            columns={columns}
            // rowClassName={({ rowIndex }) => {
            //   return rowIndex % 2 === 0 ? "row-warning" : "";
            // }}
            cellClassName={({ rowData, rowIndex }) => {
              const row = rowData as SocialProcurementTableRow;
              if (row.empty) {
                return "";
              }
              if (row.invalid) {
                return "row-error";
              }
              return row.dirty ? "row-warning" : "";
            }}
          />
        </Grid>
        <Grid
          container
          alignItems="center"
          justifyContent="flex-end"
          spacing={2}
          mt={2}
        >
          <Grid item>
            <StyledLoadingButton
              onClick={handleSave}
              loading={loading}
              disabled={loading}
            >
              Submit
            </StyledLoadingButton>
          </Grid>
          <Grid item>
            <Typography variant="body2" sx={{ m: 0 }}>
              or
            </Typography>
          </Grid>
          <Grid item>
            <Button variant="text" onClick={regenData}>
              Reset
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
